import React from 'react';
import { BookOpen, Code2, GitBranch, Lightbulb } from 'lucide-react';
import { consultantInfo } from '../mock';

const values = [
  {
    icon: Code2,
    title: 'Clean Code Advocate',
    description: 'OOP, SOLID principles, design patterns, and clean architecture shape every system I build.',
    color: 'from-emerald-500/20 to-emerald-500/5',
    iconColor: 'text-emerald-400',
    border: 'hover:border-emerald-500/30',
  },
  {
    icon: GitBranch,
    title: 'Open Source Enthusiast',
    description: 'I love exploring and contributing to open source. Sharing code and ideas with the community energizes me.',
    color: 'from-violet-500/20 to-violet-500/5',
    iconColor: 'text-violet-400',
    border: 'hover:border-violet-500/30',
  },
  {
    icon: Lightbulb,
    title: 'Curious Explorer',
    description: 'From event sourcing to distributed systems, I constantly explore new corners of software engineering.',
    color: 'from-amber-500/20 to-amber-500/5',
    iconColor: 'text-amber-400',
    border: 'hover:border-amber-500/30',
  },
  {
    icon: BookOpen,
    title: 'Teacher at Heart',
    description: 'Mentoring engineers is where I find the most fulfillment. Helping someone have an "aha!" moment never gets old.',
    color: 'from-sky-500/20 to-sky-500/5',
    iconColor: 'text-sky-400',
    border: 'hover:border-sky-500/30',
  },
];

const techStack = ['Java', 'Spring Boot', 'React', 'Microservices', 'Kafka', 'Docker', 'GCP', 'System Design', 'TDD', 'OOP'];

const About = () => {
  return (
    <section id="about" className="py-28 px-4 sm:px-6 lg:px-8 bg-slate-950 relative overflow-hidden">
      {/* Subtle background accent */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-violet-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-start">

          {/* ── Left — Text ── */}
          <div className="space-y-8">
            <div className="reveal">
              <p className="text-xs font-mono text-emerald-400 uppercase tracking-[0.2em] mb-4">// about me</p>
              <h2 className="text-4xl sm:text-5xl font-bold text-white leading-tight mb-8">
                Crafting software with{' '}
                <span className="gradient-text">purpose</span>{' '}
                and{' '}
                <span className="gradient-text">depth.</span>
              </h2>
            </div>

            <div className="space-y-5 text-[1.05rem] text-slate-400 leading-relaxed reveal reveal-delay-1">
              <p>
                With a solid foundation from <span className="text-slate-200 font-medium">SGGS Institute of Engineering</span>, my journey has taken me through product companies and global engineering teams — working with world-class engineers to build thoughtful, impactful software.
              </p>
              <p>
                My core competencies — <span className="text-slate-200 font-medium">OOP, SOLID principles, Design Patterns, LLD & HLD</span> — aren't just interview buzzwords. They're the lens through which I approach every architecture decision and code review.
              </p>
              <p>
                Beyond code, I'm genuinely energized by <span className="text-emerald-400 font-medium">open source</span> and love exploring new corners of software engineering. As someone as passionate about <span className="text-violet-400 font-medium">teaching as engineering</span>, I find deep fulfillment in growing the engineers around me.
              </p>
            </div>

            {/* Tech stack */}
            <div className="reveal reveal-delay-2">
              <p className="text-xs text-slate-500 uppercase tracking-widest mb-3 font-mono">Tech I work with</p>
              <div className="flex flex-wrap gap-2">
                {techStack.map((tech) => (
                  <span
                    key={tech}
                    className="px-3 py-1.5 text-xs font-mono font-medium text-slate-300 bg-slate-900 border border-slate-700/60 rounded-lg hover:border-emerald-500/40 hover:text-emerald-300 transition-all duration-200"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>

            <div className="reveal reveal-delay-3">
              <a
                href={`https://${consultantInfo.linkedin}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-slate-900 border border-slate-700 hover:border-emerald-500/40 text-slate-300 hover:text-white font-semibold transition-all duration-200 text-sm"
              >
                Connect on LinkedIn →
              </a>
            </div>
          </div>

          {/* ── Right — Cards ── */}
          <div className="grid sm:grid-cols-2 gap-4">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className={`reveal reveal-delay-${index + 1} group relative p-6 rounded-2xl bg-slate-900 border border-slate-800 ${value.border} transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-black/30 overflow-hidden`}
                >
                  {/* Gradient background */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${value.color} opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl`} />

                  <div className="relative z-10">
                    <div className="w-11 h-11 rounded-xl bg-slate-800 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <Icon className={`w-5 h-5 ${value.iconColor}`} />
                    </div>
                    <h3 className="text-base font-bold text-white mb-2">{value.title}</h3>
                    <p className="text-sm text-slate-400 leading-relaxed">{value.description}</p>
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </div>
    </section>
  );
};

export default About;
